import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, GlobeStand, Lightning } from "@phosphor-icons/react";

const PORT_IMG = "https://images.unsplash.com/photo-1713859288954-3314e2fcaa7a?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function AboutPreview() {
    return (
        <section data-testid="about-preview" className="relative py-24 md:py-32 bg-[#040914]">
            <div className="wms-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                <div className="lg:col-span-5">
                    <span className="wms-overline">About — 01</span>
                    <h2 className="mt-6 font-display font-light text-4xl md:text-5xl leading-tight tracking-tight">
                        A Singapore maritime house, built for industrial scale.
                    </h2>
                    <p className="mt-7 text-white/65 leading-relaxed text-base md:text-lg">
                        Windermere Shipping Pte Ltd operates as an integrated marine transportation,
                        chartering and engineering company. From the heart of Singapore — one of the world's
                        busiest ports — we coordinate vessels, voyages, shipyards and crews to move cargo
                        with discipline, transparency and unwavering safety standards.
                    </p>

                    <ul className="mt-10 space-y-5">
                        {[
                            { Icon: ShieldCheck, t: "IMO + ISM Audited Operations", d: "Class-led compliance, vetted by major oil majors." },
                            { Icon: GlobeStand, t: "60+ Active Trade Lanes", d: "From Singapore Strait to the Atlantic Basin." },
                            { Icon: Lightning, t: "24/7 Charter Desk", d: "Real-time fixture support across time zones." },
                        ].map(({ Icon, t, d }) => (
                            <li key={t} className="flex items-start gap-4">
                                <span className="flex-none mt-1 flex items-center justify-center w-10 h-10 border border-white/10 bg-[#0B1324]">
                                    <Icon size={18} weight="duotone" className="text-[#48CAE4]" />
                                </span>
                                <div>
                                    <div className="font-display text-base text-white">{t}</div>
                                    <div className="text-sm text-white/55 mt-0.5">{d}</div>
                                </div>
                            </li>
                        ))}
                    </ul>

                    <Link
                        to="/about"
                        data-testid="about-cta"
                        className="mt-10 inline-flex items-center gap-2 text-sm uppercase tracking-[0.22em] text-[#48CAE4] hover:text-white transition-colors"
                    >
                        Read the company story
                        <ArrowUpRight size={16} weight="bold" />
                    </Link>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.7 }}
                    className="lg:col-span-7 relative"
                >
                    <div className="relative aspect-[4/5] md:aspect-[16/12] overflow-hidden border border-white/10 bg-[#0B1324]">
                        <img
                            src={PORT_IMG}
                            alt="Container port logistics"
                            className="w-full h-full object-cover scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-br from-[#040914]/30 via-transparent to-[#040914]/80" />

                        {/* Floating numeric callouts */}
                        <div className="absolute left-6 bottom-6 right-6 grid grid-cols-3 gap-3">
                            {[
                                { n: "1995", l: "Founded" },
                                { n: "240+", l: "Crew" },
                                { n: "AAA", l: "Risk Rating" },
                            ].map((s) => (
                                <div key={s.l} className="wms-glass p-4 text-center">
                                    <div className="font-display text-xl text-white">{s.n}</div>
                                    <div className="text-[10px] mt-1 tracking-[0.28em] uppercase text-white/55">{s.l}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="absolute -top-6 -left-6 hidden md:block w-32 h-32 border border-[#48CAE4]/40" />
                    <div className="absolute -bottom-6 -right-6 hidden md:block w-40 h-40 border border-white/5" />
                </motion.div>
            </div>
        </section>
    );
}
